/**
 * Header - Organism component
 * App header with navigation, user menu, notifications
 * WCAG 2.1 AA compliant
 */

import React from 'react';
import { Icon, Button } from '@/components/atoms';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@kisanlink/ui-commons';

export interface HeaderProps {
  /**
   * Application logo/name
   */
  logo?: React.ReactNode;
  /**
   * User name
   */
  userName?: string;
  /**
   * User role
   */
  userRole?: string;
  /**
   * Number of unread notifications
   */
  notificationCount?: number;
  /**
   * Navigation items
   */
  navItems?: Array<{ label: string; href: string; active?: boolean }>;
  /**
   * Callback when user menu item is clicked
   */
  onUserMenuClick?: (action: string) => void;
  /**
   * Callback when notification bell is clicked
   */
  onNotificationClick?: () => void;
  /**
   * Callback when mobile menu is toggled
   */
  onMobileMenuToggle?: () => void;
  /**
   * Callback when new sale button is clicked
   */
  onNewSaleClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  logo,
  userName = 'User',
  userRole,
  notificationCount = 0,
  navItems = [],
  onUserMenuClick,
  onNotificationClick,
  onMobileMenuToggle,
  onNewSaleClick,
}) => {
  return (
    <header className="bg-white border-b border-neutral-200 shadow-sm fixed top-0 left-0 right-0 z-10 w-full" role="banner">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between w-full">
          {/* Left section */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <button
              onClick={onMobileMenuToggle}
              className="lg:hidden p-2 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Open mobile menu"
            >
              <Icon name="Menu" size={24} aria-hidden />
            </button>

            <div className="flex-shrink-0">
              {logo || (
                <div className="flex items-center gap-2">
                  <Icon name="House" size={32} className="text-primary-600" aria-hidden />
                  <span className="text-xl font-bold text-primary-600">FPO ERP</span>
                </div>
              )}
            </div>

            {/* Navigation removed - handled by sidebar only */}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {onNewSaleClick && (
              <Button
                variant="primary"
                size="sm"
                onClick={onNewSaleClick}
                className="hidden md:flex"
              >
                <Icon name="Plus" size={16} className="mr-2" />
                New Sale
              </Button>
            )}

            <button
              onClick={onNotificationClick}
              className="relative p-2 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
            >
              <Icon name="Bell" size={20} aria-hidden />
              {notificationCount > 0 && (
                <span
                  className="absolute top-1 right-1 h-2 w-2 rounded-full bg-error-500"
                  aria-hidden="true"
                />
              )}
            </button>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-medium text-neutral-900">{userName}</span>
                {userRole && <span className="text-xs text-neutral-500">{userRole}</span>}
              </div>

              <Dropdown>
                <DropdownTrigger
                  className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
                  aria-label={`User menu for ${userName}`}
                >
                  {userName.charAt(0).toUpperCase()}
                </DropdownTrigger>
                <DropdownMenu className="mt-2">
                  <DropdownItem 
                    onClick={() => onUserMenuClick?.('profile')}
                    className="flex items-center gap-2"
                  >
                    <Icon name="User" size={16} className="text-neutral-500" />
                    Profile
                  </DropdownItem>
                  <DropdownItem 
                    onClick={() => onUserMenuClick?.('settings')}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Settings" size={16} className="text-neutral-500" />
                    Settings
                  </DropdownItem>
                  <DropdownItem 
                    onClick={() => onUserMenuClick?.('logout')}
                    className="flex items-center gap-2 text-error-600 hover:text-error-700 hover:bg-error-50"
                  >
                    <Icon name="X" size={16} className="text-error-500" />
                    Logout
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
